import { ReactNode, SVGProps, FC } from 'react';
import { useMemo } from 'react';
import tw, { styled, TwStyle } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { InformationCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Breadcrumb } from 'flowbite-react';

import {
  ButtonV2,
  IconButton,
  Title,
  TitleAndButtonWrapper,
  SkeletonTextLoading,
  V2CustomLink,
} from '@restworld/ui-ds';
import { IBadgeProps, Badge } from '@restworld/ui-ds-base';
import { useResponsiveBreakPoints } from '@restworld/utils-common';

import BackButton from '../components/back-button';
import { EmployerPrimaryLayoutProps } from './employer-primary-layout';

const BreadcrumbItem = Breadcrumb.Item;

type EmployerPrimaryHeaderProps = Pick<
  EmployerPrimaryLayoutProps,
  'pageTitle' | 'addButtonTitle' | 'onAddButtonClick' | 'addButtonLoading' | 'onInfoIconClick'
> & {
  goToPreviousPage?: () => void;
  customHeaderStyles?: TwStyle;
  showInfoIcon?: boolean;
  customTitleElement?: ReactNode;
  customRightElement?: ReactNode;
  renderCustomElement?: ReactNode;
  isKanbanPageLayout?: boolean;
  isLoading?: boolean;
  breadCrumbs?: {
    label: string;
    href?: string;
    icon?: FC<SVGProps<SVGSVGElement>>;
    truncateLabel?: boolean;
  }[];
  isLastBreadCrumbInteractive?: boolean;
  badge?: { variant: IBadgeProps['variant']; text: string };
};

const ButtonWrapper = styled.div<{ twStyle?: TwStyle }>`
  ${tw`text-neutral-700`}
  ${({ twStyle }) => twStyle || tw``}
`;

const ButtonAndTitleWrapper = styled.div<{ twStyle?: TwStyle }>`
  ${tw`flex gap-2 flex-row justify-start items-center w-fit`}
  ${({ twStyle }) => twStyle || tw``}
`;

const HeaderWrapper = styled.header<{ customHeaderStyles?: TwStyle }>`
  ${tw`flex flex-col justify-between gap-3 lg:(flex-row items-center)`}
  ${({ customHeaderStyles }) => customHeaderStyles}
`;
const BreadcrumbStyled = styled(Breadcrumb)`
  .breadcrumb-item > svg {
    ${tw`text-neutral-700 ml-0.5 mr-0.5 max-w-[1rem] max-h-4 min-w-[1rem] min-h-[1rem]`}
  }
`;
export const EmployerPrimaryLayoutHeader: FC<EmployerPrimaryHeaderProps> = ({
  pageTitle,
  addButtonTitle,
  onAddButtonClick,
  addButtonLoading,
  goToPreviousPage,
  onInfoIconClick,
  customHeaderStyles,
  showInfoIcon = true,
  customTitleElement,
  customRightElement,
  renderCustomElement,
  isKanbanPageLayout,
  isLoading,
  breadCrumbs,
  isLastBreadCrumbInteractive,
  badge,
}) => {
  const { isSmallMobile } = useResponsiveBreakPoints();
  const { t: tC } = useTranslation('common');
  const renderBreadcrumbs = useMemo(
    () =>
      breadCrumbs?.length ? (
        <BreadcrumbStyled className="breadcrumb" aria-label="breadcrumb">
          {breadCrumbs.map(({ label, href, truncateLabel, icon }, i) => {
            const breadcrumbTitle = (
              <Title
                maxChars={isSmallMobile && truncateLabel ? 12 : null}
                content={label}
                fontSize="small"
                fontWeight="medium"
                textColor="neutral-700"
              />
            );
            return (
              <BreadcrumbItem
                key={label + i}
                {...{
                  as: 'li',
                }}
                className={`breadcrumb-item breadcrumb-item-${i}`}
              >
                {breadCrumbs.length - 1 === i && !isLastBreadCrumbInteractive ? (
                  breadcrumbTitle
                ) : (
                  <V2CustomLink href={href} StartIcon={icon} newTab={false} {...{ as: 'div' }}>
                    {breadcrumbTitle}
                  </V2CustomLink>
                )}
              </BreadcrumbItem>
            );
          })}
        </BreadcrumbStyled>
      ) : null,
    [breadCrumbs, isLastBreadCrumbInteractive, isSmallMobile]
  );

  const renderBackButton = useMemo(
    () =>
      goToPreviousPage ? (
        <ButtonWrapper>
          <BackButton twStyle={tw`relative`} onClick={goToPreviousPage} />
        </ButtonWrapper>
      ) : null,
    [goToPreviousPage]
  );

  const renderTitleAndInfoButton = useMemo(
    () =>
      isLoading ? (
        <div tw="flex flex-col gap-2">
          <SkeletonTextLoading width="rect-box" />
          <SkeletonTextLoading width="long" />
        </div>
      ) : (
        (customTitleElement || pageTitle) && (
          <TitleAndButtonWrapper>
            {customTitleElement || (
              <>
                <Title fontSize="superSuperLarge" fontWeight="semibold">
                  {pageTitle ? tC(pageTitle) : ''}
                </Title>
                {showInfoIcon && (
                  <IconButton Icon={InformationCircleIcon} ariaLabel={tC('view_info')} onClick={onInfoIconClick} />
                )}
                {badge && (
                  <Badge variant={badge.variant} tw="w-fit">
                    {badge.text}
                  </Badge>
                )}
              </>
            )}
          </TitleAndButtonWrapper>
        )
      ),
    [isLoading, customTitleElement, pageTitle, tC, showInfoIcon, onInfoIconClick, badge]
  );

  const renderAddButton = useMemo(
    () =>
      !!addButtonTitle && (
        <ButtonV2
          StartIcon={PlusIcon}
          twStyle={tw`mt-4 w-full max-w-full lg:(w-fit)`}
          onClick={onAddButtonClick}
          loading={addButtonLoading}
          id="layout-cta-button"
        >
          {tC(addButtonTitle)}
        </ButtonV2>
      ),
    [addButtonTitle, onAddButtonClick, addButtonLoading, tC]
  );
  const isBreadcrumbPresent = breadCrumbs?.length > 0;
  if (isKanbanPageLayout)
    return (
      <>
        <div tw="block lg:hidden">
          <HeaderWrapper tw="flex flex-col gap-3" customHeaderStyles={customHeaderStyles}>
            <div tw="w-full flex flex-row justify-between gap-2 items-center">
              {renderBackButton}
              {customRightElement}
            </div>
            <ButtonAndTitleWrapper tw="flex-col items-start justify-center">
              {renderBreadcrumbs}
              {renderTitleAndInfoButton}
            </ButtonAndTitleWrapper>
          </HeaderWrapper>
          {renderCustomElement}
        </div>
        <div tw="hidden lg:block">
          <div tw="flex flex-row gap-4 items-center mb-3">
            {renderBackButton}
            {renderBreadcrumbs}
          </div>
          <HeaderWrapper
            tw="pt-0 pb-[16px] justify-start flex flex-row items-center"
            customHeaderStyles={customHeaderStyles}
          >
            <ButtonAndTitleWrapper tw="flex-row justify-start items-center">
              {renderTitleAndInfoButton}
              {customRightElement}
            </ButtonAndTitleWrapper>
          </HeaderWrapper>
          {renderCustomElement}
        </div>
      </>
    );
  return (
    <>
      <HeaderWrapper customHeaderStyles={customHeaderStyles}>
        {!isBreadcrumbPresent ? (
          <div tw="flex flex-col gap-4">
            <ButtonAndTitleWrapper>
              {renderBackButton}
              {renderTitleAndInfoButton}
            </ButtonAndTitleWrapper>
            {renderCustomElement}
          </div>
        ) : (
          <>
            <div tw="block lg:hidden">
              {renderBackButton}
              {renderBreadcrumbs}
              <ButtonAndTitleWrapper>{renderTitleAndInfoButton}</ButtonAndTitleWrapper>
            </div>
            <div tw="hidden lg:block">
              <ButtonAndTitleWrapper>
                {renderBackButton}
                {renderTitleAndInfoButton}
              </ButtonAndTitleWrapper>
              {renderBreadcrumbs}
            </div>
          </>
        )}
        {customRightElement}
        {renderAddButton}
      </HeaderWrapper>
    </>
  );
};

export default EmployerPrimaryLayoutHeader;
